import { Vector3 } from "three";

export type AvatarState = "idle" | "walk" | "turn" | "sit" | "observe";
export type AvatarPose = "sit" | "observe" | null;

const WALK_SPEED = 1.8; // m/s (ritmo demo: el avatar llega al puesto dentro del dwell de la etapa)
const TURN_SPEED = 4.5; // rad/s (rotación suave)
const TURN_CLIP_THRESHOLD = (120 * Math.PI) / 180; // giros > 120° usan clip "turn"

/**
 * Controlador de movimiento y pose del avatar.
 * Máquina idle/walk/turn/sit/observe: el movimiento sigue waypoints a
 * 1.3 m/s con rotación suave y los pies siempre al suelo (y = 0).
 * "turn" se reserva para giros mayores de 120°; "observe" para las
 * etapas de preoperacional y pistas; "sit" al llegar a un puesto.
 */
export class AvatarController {
  readonly position = new Vector3();
  yaw = 0;
  state: AvatarState = "idle";

  private target: Vector3 | null = null;
  private pose: AvatarPose = null;

  constructor(start: [number, number, number]) {
    this.position.set(start[0], 0, start[2]);
  }

  get currentPose(): AvatarPose {
    return this.pose;
  }

  get isSeated(): boolean {
    return this.pose === "sit";
  }

  /** Camina hasta la posición indicada (y se ignora: los pies van al suelo). */
  goTo(position: [number, number, number]): void {
    this.target = new Vector3(position[0], 0, position[2]);
    this.pose = null;
    if (this.state === "sit" || this.state === "observe") this.stand();
  }

  sit(_seat?: unknown): void {
    this.target = null;
    this.pose = "sit";
    this.state = "sit";
  }

  observe(): void {
    this.target = null;
    this.pose = "observe";
    this.state = "observe";
  }

  stand(): void {
    this.pose = null;
    this.state = "idle";
  }

  /** Avanza la simulación; devuelve true si hubo cambio de pose/posición. */
  update(delta: number): boolean {
    if (!this.target) {
      if (this.state !== "sit" && this.state !== "observe") this.state = "idle";
      return false;
    }
    const toTarget = new Vector3().subVectors(this.target, this.position);
    toTarget.y = 0;
    const dist = toTarget.length();
    if (dist < 0.05) {
      this.position.copy(this.target);
      this.target = null;
      this.state = this.pose ?? "idle";
      return true;
    }
    // giro suave hacia el rumbo
    const desiredYaw = Math.atan2(toTarget.x, toTarget.z);
    let dyaw = desiredYaw - this.yaw;
    while (dyaw > Math.PI) dyaw -= Math.PI * 2;
    while (dyaw < -Math.PI) dyaw += Math.PI * 2;
    const maxTurn = TURN_SPEED * delta;
    this.yaw += Math.min(maxTurn, Math.max(-maxTurn, dyaw));
    this.state = Math.abs(dyaw) > TURN_CLIP_THRESHOLD ? "turn" : "walk";
    // avance
    const step = Math.min(WALK_SPEED * delta, dist);
    this.position.addScaledVector(toTarget.normalize(), step);
    this.position.y = 0;
    return true;
  }
}
